import { useState } from 'react';
import { currencyGlyph } from '@/lib/money/index.js';
import { useMoneyCurrency } from '@/components/money/MoneyProvider.js';
import { useTranslation } from 'react-i18next';
import { useMoney } from '../../../lib/money/useMoney.js';
import { minorToPlain } from '../state/bet.js';

export interface BetLimits {
  min: bigint;
  max: bigint;
  step: bigint;
}

export function clampBet(value: bigint, limits: BetLimits): bigint {
  let v = value;
  if (v < limits.min) v = limits.min;
  if (v > limits.max) v = limits.max;
  if (limits.step > 0n) {
    const snapped = limits.min + ((v - limits.min) / limits.step) * limits.step;
    v = snapped;
  }
  return v;
}

/** Clamp to the [min,max] band only — used while the player is typing, so the
 *  step-snap (which would fight mid-entry) is deferred to blur. */
function clampRange(value: bigint, limits: BetLimits): bigint {
  if (value < limits.min) return limits.min;
  if (value > limits.max) return limits.max;
  return value;
}

interface Props {
  value: bigint;
  limits: BetLimits;
  balance: bigint | null;
  disabled?: boolean;
  /** When the game's bet limits are a fixed option list (e.g. scratch), render chips that
   *  snap to an allowed value — the stepper could otherwise produce a non-option bet the
   *  backend rejects with ORIG-002. */
  options?: bigint[] | null;
  onChange: (v: bigint) => void;
}

export function AmountField({ value, limits, balance, disabled, options, onChange }: Props) {
  const { t } = useTranslation('originals');
  const fieldCurrency = useMoneyCurrency();
  const { fromMinor, toMinor, multiplier } = useMoney();
  const mult = multiplier();
  const set = (v: bigint) => onChange(clampBet(v, limits));

  // Local edit buffer: while focused, show exactly what the player typed; on
  // blur, resync to the canonical formatted value.
  const [draft, setDraft] = useState<string | null>(null);

  const Header = (
    <div className="flex items-center justify-between text-[12px]">
      <span className="font-medium text-[var(--text-secondary)]">{t('panel.amount')}</span>
      {balance != null && (
        <span className="text-[var(--text-muted)]">
          {t('balance')} <span className="font-semibold text-[var(--text-secondary)]">{fromMinor(balance)}</span>
        </span>
      )}
    </div>
  );

  if (options && options.length) {
    return (
      <div className="flex flex-col gap-2">
        {Header}
        <div className="flex flex-wrap gap-2">
          {options.map((opt) => {
            const active = opt === value;
            return (
              <button
                key={String(opt)}
                type="button"
                disabled={disabled}
                aria-pressed={active}
                onClick={() => onChange(opt)}
                className={[
                  'h-11 px-4 rounded-xl text-[14px] font-bold tabular-nums transition-all disabled:opacity-40',
                  active
                    ? 'bg-[var(--primary)] text-[var(--primary-on)] shadow-[0_4px_16px_var(--primary-glow)]'
                    : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:bg-[var(--bg-raised)] hover:text-[var(--text-primary)]',
                ].join(' ')}
              >
                {minorToPlain(opt, mult)}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const quickBtn =
    'h-8 px-3 rounded-lg bg-[var(--bg-sunken)] text-[12px] font-bold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-raised)] hover:text-[var(--text-primary)] disabled:opacity-40';

  const onType = (raw: string) => {
    setDraft(raw);
    const s = raw.trim();
    if (!s) return; // empty mid-edit — keep last committed value
    try {
      onChange(clampRange(toMinor(s), limits));
    } catch {
      /* partial input (e.g. "1.") — ignore until parseable */
    }
  };

  // Quick set-to presets (bc.game style). Round "nice" major-unit amounts inside the
  // limit band; ≤4 chips, spread across the range. Numbers only → no i18n needed.
  const minMajor = Number(limits.min) / Number(mult);
  const maxMajor = Number(limits.max) / Number(mult);
  const candidates = [1, 5, 10, 50, 100, 500, 1000, 5000, 10000, 50000];
  const within = candidates.filter((c) => c >= minMajor && c <= maxMajor);
  const presets =
    within.length <= 4
      ? within
      : [...new Set([0, 1, 2, 3].map((i) => within[Math.round((i * (within.length - 1)) / 3)]!))];
  const fmtPreset = (n: number) => (n >= 1000 ? `${(n / 1000).toString()}K` : String(n));

  return (
    <div className="flex flex-col gap-2">
      {Header}
      <div className="flex items-center gap-2 h-14 pl-3.5 pr-2 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] transition-colors focus-within:border-[var(--primary)] focus-within:shadow-[0_0_0_3px_var(--primary-glow)]">
        {/* currencyGlyph, not a hardcoded ฿ — a JPY brand renders ¥ (WEB-25). */}
        <span className="text-[18px] font-extrabold text-[var(--warning)]">
          {currencyGlyph(fieldCurrency)}
        </span>
        {/* bare decimal (minorToPlain), NOT fromMinor — fromMinor emits "THB 1,234.50" → double currency */}
        <input
          type="text"
          inputMode="decimal"
          disabled={disabled}
          aria-label={t('panel.amount')}
          value={draft ?? minorToPlain(value, mult)}
          onChange={(e) => onType(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={() => { set(value); setDraft(null); }}
          className="min-w-0 flex-1 bg-transparent text-[17px] font-extrabold tabular-nums text-[var(--text-primary)] outline-none disabled:opacity-40"
        />
        <div className="flex shrink-0 gap-1.5">
          <button type="button" disabled={disabled} onClick={() => set(value / 2n)} className={quickBtn}>{t('panel.half')}</button>
          <button type="button" disabled={disabled} onClick={() => set(value * 2n)} className={quickBtn}>{t('panel.double')}</button>
          <button type="button" disabled={disabled}
            onClick={() => set(balance != null && balance < limits.max ? balance : limits.max)} className={quickBtn}>{t('panel.max')}</button>
        </div>
      </div>
      {presets.length > 1 && (
        <div className="flex gap-1.5">
          {presets.map((p) => (
            <button
              key={p}
              type="button"
              disabled={disabled}
              onClick={() => set(BigInt(p) * mult)}
              className="h-8 flex-1 rounded-lg bg-[var(--bg-sunken)] text-[12px] font-bold tabular-nums text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-raised)] hover:text-[var(--text-primary)] disabled:opacity-40"
            >
              {fmtPreset(p)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
